import { useMemo, useState } from "react";
import { useApi } from "@/hooks/use-api";
import { apiFetch } from "@/lib/api-client";
import { resolveBlock, type BehavioralSignal, type ContentBlock } from "@ease/messaging-core/behavioral";

interface DynamicSignalMatch {
  source: "scroll_depth" | "pages_visited" | "webinar_attendance" | "form_fields" | "conversation_keywords" | "utm_campaign" | "deal_stage" | "purchase_history" | "engagement_velocity" | "channel_preference" | "default";
  operator: "eq" | "gt" | "lt" | "gte" | "lte" | "contains" | "exists";
  value?: string;
}

interface DynamicVariant {
  id: string;
  label: string;
  subject?: string;
  body: string;
  priority: number;
  signalMatch: DynamicSignalMatch;
}

interface DynamicBlock {
  id: string;
  name: string;
  description: string | null;
  defaultVariantId: string;
  variants: DynamicVariant[];
  createdAt: string;
  updatedAt: string;
}

interface BlockDraft {
  name: string;
  description: string;
  defaultVariantId: string;
  variants: DynamicVariant[];
}

interface DynamicPreviewCase {
  id: string;
  label: string;
  description: string;
  signals: BehavioralSignal[];
}

const EMPTY_VARIANT: DynamicVariant = {
  id: "default",
  label: "Default",
  body: "<p>Hello {{firstName}}</p>",
  priority: 100,
  signalMatch: {
    source: "default",
    operator: "exists",
  },
};

const EMPTY_DRAFT: BlockDraft = {
  name: "",
  description: "",
  defaultVariantId: "default",
  variants: [EMPTY_VARIANT],
};

const PREVIEW_CASES: DynamicPreviewCase[] = [
  {
    id: "utm-campaign",
    label: "UTM campaign",
    description: "Preview for a visitor arriving from a tagged campaign.",
    signals: [{ source: "utm_campaign", key: "utm_campaign", value: "spring_whitening", confidence: 1, collectedAt: "2026-04-16T08:00:00.000Z" }],
  },
  {
    id: "deal-stage",
    label: "Deal stage",
    description: "Preview for a contact already deep in the pipeline.",
    signals: [{ source: "deal_stage", key: "deal_stage", value: "60", confidence: 0.92, collectedAt: "2026-04-16T08:00:00.000Z" }],
  },
  {
    id: "engagement-velocity",
    label: "Engagement velocity",
    description: "Preview for a contact with strong recent activity.",
    signals: [{ source: "engagement_velocity", key: "engagement_velocity", value: "85", confidence: 0.88, collectedAt: "2026-04-16T08:00:00.000Z" }],
  },
  {
    id: "channel-preference",
    label: "Channel preference",
    description: "Preview for a contact that prefers SMS or fast mobile follow-up.",
    signals: [{ source: "channel_preference", key: "channel_preference", value: "sms", confidence: 0.95, collectedAt: "2026-04-16T08:00:00.000Z" }],
  },
  {
    id: "fallback",
    label: "Fallback",
    description: "Preview when no behavioral rule matches and the default variant wins.",
    signals: [],
  },
];

function toContentBlock(draft: BlockDraft): ContentBlock {
  return {
    id: "preview-block",
    name: draft.name || "Untitled block",
    defaultVariantId: draft.defaultVariantId,
    variants: draft.variants.map((variant) => ({
      id: variant.id,
      label: variant.label,
      subject: variant.subject,
      body: variant.body,
      priority: variant.priority,
      signalMatch: variant.signalMatch,
    })),
  };
}

export default function DynamicBlocksPage() {
  const { data, isLoading, error, refetch } = useApi<DynamicBlock[]>("/email-marketing/dynamic-blocks");
  const [showEditor, setShowEditor] = useState(false);
  const [draft, setDraft] = useState<BlockDraft>(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const blocks = useMemo(() => data ?? [], [data]);

  const openCreate = () => {
    setDraft(EMPTY_DRAFT);
    setEditingId(null);
    setStatusMessage(null);
    setShowEditor(true);
  };

  const openEdit = (block: DynamicBlock) => {
    setDraft({
      name: block.name,
      description: block.description ?? "",
      defaultVariantId: block.defaultVariantId,
      variants: block.variants,
    });
    setEditingId(block.id);
    setStatusMessage(null);
    setShowEditor(true);
  };

  const saveDraft = async () => {
    setIsSaving(true);
    setStatusMessage(null);
    const payload = {
      name: draft.name.trim(),
      description: draft.description.trim() || undefined,
      defaultVariantId: draft.defaultVariantId,
      variants: draft.variants,
    };

    try {
      if (editingId) {
        await apiFetch(`/email-marketing/dynamic-blocks/${editingId}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
        setStatusMessage("Dynamic block updated.");
      } else {
        await apiFetch("/email-marketing/dynamic-blocks", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        setStatusMessage("Dynamic block created.");
      }
      setShowEditor(false);
      refetch();
    } catch (saveError) {
      setStatusMessage(saveError instanceof Error ? saveError.message : "Failed to save dynamic block.");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteBlock = async (id: string) => {
    setStatusMessage(null);
    try {
      await apiFetch(`/email-marketing/dynamic-blocks/${id}`, { method: "DELETE" });
      setStatusMessage("Dynamic block deleted.");
      refetch();
    } catch (deleteError) {
      setStatusMessage(deleteError instanceof Error ? deleteError.message : "Failed to delete dynamic block.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Dynamic Content Blocks</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage behavioral content variants used across email templates with the same signal-driven resolution model as the page builder.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          New block
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="h-28 animate-pulse rounded-xl border border-border bg-muted" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Failed to load dynamic content blocks. {error}
        </div>
      ) : blocks.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
          <h2 className="text-base font-semibold text-foreground">No dynamic content blocks yet</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Create your first block to personalize subject lines or body sections by UTM, deal stage, engagement velocity, or channel preference.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {blocks.map((block) => (
            <div key={block.id} className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-base font-semibold text-foreground">{block.name}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {block.description || "No description"} · {block.variants.length} variant{block.variants.length === 1 ? "" : "s"} · Updated {new Date(block.updatedAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => openEdit(block)} className="rounded-md border border-border px-3 py-1.5 text-xs text-foreground">Edit</button>
                  <button type="button" onClick={() => void deleteBlock(block.id)} className="rounded-md border border-destructive/30 px-3 py-1.5 text-xs text-destructive">Delete</button>
                </div>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {block.variants.map((variant) => (
                  <div key={variant.id} className="rounded-lg bg-[var(--color-surface)] p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{variant.label}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {variant.signalMatch.source} {variant.signalMatch.operator} {variant.signalMatch.value ?? "present"}
                        </p>
                      </div>
                      {block.defaultVariantId === variant.id && (
                        <span className="rounded-full bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary">Default</span>
                      )}
                    </div>
                    {variant.subject && (
                      <p className="mt-2 text-xs text-foreground">Subject: {variant.subject}</p>
                    )}
                    <div className="mt-2 rounded-md bg-background px-3 py-2 text-xs text-muted-foreground">
                      {variant.body.slice(0, 180)}
                      {variant.body.length > 180 ? "..." : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {statusMessage && (
        <p className={`text-sm ${statusMessage.includes("Failed") ? "text-destructive" : "text-[var(--color-success)]"}`}>
          {statusMessage}
        </p>
      )}

      {showEditor && (
        <BlockEditorModal
          draft={draft}
          isSaving={isSaving}
          onCancel={() => setShowEditor(false)}
          onChange={setDraft}
          onSubmit={() => void saveDraft()}
        />
      )}
    </div>
  );
}

function BlockEditorModal({
  draft,
  isSaving,
  onCancel,
  onChange,
  onSubmit,
}: {
  draft: BlockDraft;
  isSaving: boolean;
  onCancel: () => void;
  onChange: (draft: BlockDraft) => void;
  onSubmit: () => void;
}) {
  const contentBlock = useMemo(() => toContentBlock(draft), [draft]);
  const previewCases = useMemo(() => (
    PREVIEW_CASES.map((previewCase) => {
      const resolution = resolveBlock(contentBlock, previewCase.signals);
      const variant = draft.variants.find((entry) => entry.id === resolution.selectedVariantId) ?? draft.variants[0] ?? EMPTY_VARIANT;
      return {
        ...previewCase,
        resolution,
        variant,
      };
    })
  ), [contentBlock, draft.variants]);

  const updateVariant = (index: number, variant: DynamicVariant) => {
    onChange({
      ...draft,
      variants: draft.variants.map((entry, currentIndex) => currentIndex === index ? variant : entry),
    });
  };

  const addVariant = () => {
    const id = `variant-${Date.now()}`;
    onChange({
      ...draft,
      variants: [
        ...draft.variants,
        {
          ...EMPTY_VARIANT,
          id,
          label: "New variant",
          signalMatch: { source: "utm_campaign", operator: "contains", value: "" },
        },
      ],
    });
  };

  const removeVariant = (index: number) => {
    const nextVariants = draft.variants.filter((_, currentIndex) => currentIndex !== index);
    onChange({
      ...draft,
      variants: nextVariants,
      defaultVariantId: nextVariants[0]?.id ?? draft.defaultVariantId,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">Dynamic block editor</h2>
          <button type="button" onClick={onCancel} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>

        <div className="space-y-5 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase text-muted-foreground">Block name</span>
              <input
                type="text"
                value={draft.name}
                onChange={(event) => onChange({ ...draft, name: event.target.value })}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase text-muted-foreground">Default variant</span>
              <select
                value={draft.defaultVariantId}
                onChange={(event) => onChange({ ...draft, defaultVariantId: event.target.value })}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                {draft.variants.map((variant) => (
                  <option key={variant.id} value={variant.id}>{variant.label}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="space-y-1">
            <span className="text-xs font-medium uppercase text-muted-foreground">Description</span>
            <textarea
              rows={2}
              value={draft.description}
              onChange={(event) => onChange({ ...draft, description: event.target.value })}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium uppercase text-muted-foreground">Variants</p>
              <button type="button" onClick={addVariant} className="text-xs font-medium text-primary">+ Add variant</button>
            </div>
            {draft.variants.map((variant, index) => (
              <div key={variant.id} className="space-y-3 rounded-xl border border-border p-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <label className="space-y-1">
                    <span className="text-xs font-medium uppercase text-muted-foreground">Label</span>
                    <input
                      type="text"
                      value={variant.label}
                      onChange={(event) => updateVariant(index, { ...variant, label: event.target.value })}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="space-y-1">
                    <span className="text-xs font-medium uppercase text-muted-foreground">Signal source</span>
                    <select
                      value={variant.signalMatch.source}
                      onChange={(event) => updateVariant(index, {
                        ...variant,
                        signalMatch: {
                          ...variant.signalMatch,
                          source: event.target.value as DynamicSignalMatch["source"],
                        },
                      })}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    >
                      <option value="default">Default</option>
                      <option value="utm_campaign">UTM campaign</option>
                      <option value="deal_stage">Deal stage</option>
                      <option value="engagement_velocity">Engagement velocity</option>
                      <option value="channel_preference">Channel preference</option>
                      <option value="conversation_keywords">Conversation keywords</option>
                    </select>
                  </label>
                  <label className="space-y-1">
                    <span className="text-xs font-medium uppercase text-muted-foreground">Operator</span>
                    <select
                      value={variant.signalMatch.operator}
                      onChange={(event) => updateVariant(index, {
                        ...variant,
                        signalMatch: {
                          ...variant.signalMatch,
                          operator: event.target.value as DynamicSignalMatch["operator"],
                        },
                      })}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    >
                      <option value="exists">Exists</option>
                      <option value="eq">Equals</option>
                      <option value="contains">Contains</option>
                      <option value="gt">Greater than</option>
                      <option value="gte">Greater or equal</option>
                      <option value="lt">Less than</option>
                      <option value="lte">Less or equal</option>
                    </select>
                  </label>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="space-y-1">
                    <span className="text-xs font-medium uppercase text-muted-foreground">Signal value</span>
                    <input
                      type="text"
                      value={variant.signalMatch.value ?? ""}
                      onChange={(event) => updateVariant(index, {
                        ...variant,
                        signalMatch: { ...variant.signalMatch, value: event.target.value || undefined },
                      })}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="space-y-1">
                    <span className="text-xs font-medium uppercase text-muted-foreground">Subject override</span>
                    <input
                      type="text"
                      value={variant.subject ?? ""}
                      onChange={(event) => updateVariant(index, { ...variant, subject: event.target.value || undefined })}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    />
                  </label>
                </div>
                <label className="space-y-1">
                  <span className="text-xs font-medium uppercase text-muted-foreground">Variant body</span>
                  <textarea
                    rows={5}
                    value={variant.body}
                    onChange={(event) => updateVariant(index, { ...variant, body: event.target.value })}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  />
                </label>
                <div className="flex justify-end">
                  <button
                    type="button"
                    disabled={draft.variants.length === 1}
                    onClick={() => removeVariant(index)}
                    className="rounded-md border border-destructive/30 px-3 py-1.5 text-xs text-destructive disabled:opacity-50"
                  >
                    Remove variant
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-3 rounded-xl border border-[var(--color-ai-border)] bg-[var(--color-ai-light)] p-4">
            <div>
              <p className="text-xs font-medium uppercase text-[var(--color-ai)]">Rule preview</p>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Validate which variant wins before save. These scenarios use the live behavioral resolver, not a UI-only approximation.
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {previewCases.map((previewCase) => (
                <div key={previewCase.id} className="rounded-xl border border-white/60 bg-white/70 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-[var(--color-foreground)]">{previewCase.label}</p>
                      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{previewCase.description}</p>
                    </div>
                    <span className="rounded-full bg-[var(--color-background)] px-2 py-1 text-[11px] font-medium text-[var(--color-foreground)]">
                      {previewCase.resolution.reason === "signal_match" ? "Matched" : "Fallback"}
                    </span>
                  </div>
                  <div className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-3">
                    <p className="text-[11px] font-medium uppercase text-[var(--color-muted-foreground)]">Selected variant</p>
                    <p className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">{previewCase.variant.label}</p>
                    {previewCase.variant.subject ? (
                      <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">Subject: {previewCase.variant.subject}</p>
                    ) : null}
                    <div className="mt-2 rounded-md bg-[var(--color-surface)] px-3 py-2 text-xs text-[var(--color-foreground)]">
                      {previewCase.variant.body.slice(0, 140)}
                      {previewCase.variant.body.length > 140 ? "..." : ""}
                    </div>
                  </div>
                  <p className="mt-2 text-[11px] text-[var(--color-muted-foreground)]">
                    {previewCase.resolution.matchedSignal
                      ? `Matched ${previewCase.resolution.matchedSignal.source} = ${previewCase.resolution.matchedSignal.value}`
                      : "No rule matched. Default variant stays visible."}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
          <button type="button" onClick={onCancel} className="rounded-md border border-border px-4 py-2 text-sm">
            Cancel
          </button>
          <button
            type="button"
            disabled={!draft.name.trim() || draft.variants.length === 0 || isSaving}
            onClick={onSubmit}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Save block"}
          </button>
        </div>
      </div>
    </div>
  );
}
